export type Language = 'en' | 'fr';

export const translations = {
  en: {
    common: {
      search: 'Search...',
      refresh: 'Refresh',
      loading: 'Loading…',
      save: 'Save',
      cancel: 'Cancel',
      delete: 'Delete',
      edit: 'Edit',
      close: 'Close',
      actions: 'Actions',
      status: 'Status',
      active: 'Active',
      inactive: 'Inactive',
      enabled: 'Enabled',
      disabled: 'Disabled',
      yes: 'Yes',
      no: 'No',
      date: 'Date',
      type: 'Type',
      amount: 'Amount',
      currency: 'Currency',
      id: 'ID',
      name: 'Name',
      email: 'Email',
      phone: 'Phone',
      country: 'Country',
      noData: 'No data available',
      allowed: 'Allowed',
      blocked: 'Blocked',
      all: 'All',
      pending: 'Pending',
      approved: 'Approved',
      rejected: 'Rejected',
      completed: 'Completed',
      failed: 'Failed',
      cancelled: 'Cancelled',
      verified: 'Verified',
      suspended: 'Suspended',
      notSubmitted: 'Not Submitted',
      markAllRead: 'Mark all read',
      notifications: 'Notifications',
      myProfile: 'My Profile',
      settings: 'Settings',
      logOut: 'Log Out',
      superAdmin: 'Super Admin',
      saving: 'Saving…',
      on: 'On',
      off: 'Off',
      view: 'View',
      details: 'Details',
      address: 'Address',
      city: 'City',
      back: 'Back',
      add: 'Add',
      update: 'Update',
      agent: 'Agent',
    },
    sidebar: {
      main: 'MAIN',
      management: 'MANAGEMENT',
      configuration: 'CONFIGURATION',
      support: 'SUPPORT',
      system: 'SYSTEM',
      dashboard: 'Dashboard',
      analytics: 'Analytics',
      users: 'Users',
      agents: 'Agents',
      agencies: 'Agencies',
      transactions: 'Transactions',
      compliance: 'Compliance',
      kycReview: 'KYC Review',
      wallets: 'Wallets',
      cards: 'Cards',
      splitPayments: 'Split Payments',
      chatSupport: 'Chat Support',
      helpArticles: 'Help Articles',
      corridors: 'Corridors',
      exchangeRates: 'Exchange Rates',
      feesCharges: 'Fees & Charges',
      countries: 'Countries',
      cryptoSettings: 'Crypto Settings',
      adminAccounts: 'Admin Accounts',
      auditLog: 'Audit Log',
    },
    login: {
      title: 'Admin Dashboard',
      subtitle: 'Sign in to manage your platform',
      emailLabel: 'Email Address',
      emailPlaceholder: 'admin@moneyplus.cd',
      passwordLabel: 'Password',
      passwordPlaceholder: 'Enter your password',
      forgotPassword: 'Forgot password?',
      signIn: 'Sign In',
      signingIn: 'Signing in...',
      emailRequired: 'Please enter both email and password.',
      invalidCredentials: 'Invalid email or password.',
      noToken: 'Login succeeded but no token was returned by the server.',
      loginFailed: 'Login failed. Please try again.',
      forgotMessage: 'Password reset link sent to your email.',
      version: 'Money+ Congo Admin Panel v1.0',
    },
    dashboard: {
      title: 'Dashboard',
      subtitle: 'Overview of Money+ Congo',
      totalUsers: 'Total Users',
      totalTransactions: 'Total Transactions',
      transactionVolume: 'Transaction Volume',
      revenue: 'Revenue (Fees)',
      dailyVolume: 'Daily Volume (Last 7 days)',
      byType: 'Transactions by Type',
      topCountries: 'Top Countries (Users)',
      recentTransactions: 'Recent Transactions',
      noRecentTx: 'No recent transactions.',
      failedToLoad: 'Failed to load dashboard',
    },
    analytics: {
      title: 'Analytics',
      subtitle: 'Live metrics from the backend',
      totalVolume: 'Total Volume (CDF)',
      activeUsers: 'Active Users',
      totalTransactions: 'Total Transactions',
      totalFees: 'Total Fees (CDF)',
      byType: 'Transactions by Type',
      topCountries: 'Top Countries',
      dailyCount: 'Daily Transaction Count',
      failedToLoad: 'Failed to load analytics',
    },
    users: {
      title: 'User Management',
      searchPlaceholder: 'Search users...',
      addUser: 'Add User',
      addUserDesc: 'Create a new user account',
      countryCode: 'Country Code',
      password: 'Password',
      avatarUrl: 'Avatar URL',
      kycPhotos: 'KYC Photos',
      kycBack: 'ID Back',
      totalUsers: 'Total Users',
      activeUsers: 'Active Users',
      suspended: 'Suspended',
      pendingKyc: 'Pending KYC',
      noUsers: 'No users found.',
      failedToLoad: 'Failed to load users',
      columns: {
        user: 'User',
        phone: 'Phone',
        country: 'Country',
        kyc: 'KYC',
        walletBalance: 'Wallet Balance',
        status: 'Status',
        joined: 'Joined',
        actions: 'Actions',
      },
      kycStatus: {
        verified: 'Verified',
        pending: 'Pending',
        rejected: 'Rejected',
        not_submitted: 'Not Submitted',
      },
      userStatus: {
        active: 'Active',
        suspended: 'Suspended',
        pending: 'Pending',
      },
      detail: {
        personalInfo: 'Personal Information',
        wallets: 'Wallets',
        transactions: 'Recent Transactions',
        kycDetails: 'KYC Details',
        fullName: 'Full Name',
        email: 'Email',
        phone: 'Phone',
        country: 'Country',
        joined: 'Joined',
        walletBalance: 'Wallet Balance',
        kycStatus: 'KYC Status',
        userStatus: 'Account Status',
        suspend: 'Suspend User',
        activate: 'Activate User',
        noTransactions: 'No transactions.',
        noWallets: 'No wallets.',
      },
    },
    agents: {
      title: 'Agent Management',
      searchPlaceholder: 'Search agents...',
      totalAgents: 'Total Agents',
      totalCommissions: 'Total Commissions',
      todayTx: "Today's Transactions",
      activeFloat: 'Active Float',
      noAgents: 'No agents found',
      cashFloat: 'Cash Float',
      totalCommission: 'Total Commission',
      recentTx: 'Recent Agent Transactions',
      commissionHistory: 'Commission History',
      columns: {
        agent: 'Agent',
        phone: 'Phone',
        country: 'Country',
        cashFloat: 'Cash Float',
        eMoneyFloat: 'E-Money Float',
        commission: 'Commission',
        todayTxns: 'Today Txns',
        actions: 'Actions',
      },
      failedToLoad: 'Failed to load agents',
    },
    agencies: {
      title: 'Agency Management',
      newAgency: 'New Agency',
      editAgency: 'Edit Agency',
      searchPlaceholder: 'Search by name, city or code…',
      nameRequired: 'Name and city are required.',
      failedToSave: 'Failed to save',
      columns: {
        code: 'Code',
        name: 'Name',
        type: 'Type',
        city: 'City',
        manager: 'Manager',
        status: 'Status',
        actions: 'Actions',
      },
      types: {
        agency: 'Agency',
        partner_branch: 'Partner Branch',
        mobile_agent: 'Mobile Agent',
      },
      fields: {
        type: 'Type',
        name: 'Name',
        city: 'City',
        address: 'Address',
        managerName: 'Manager Name',
        managerPhone: 'Manager Phone',
        cashLimit: 'Cash Limit (XAF)',
        openingHours: 'Opening Hours',
        agentUserId: 'Agent User ID (optional)',
      },
    },
    transactions: {
      title: 'Transactions',
      searchPlaceholder: 'Search by reference or description...',
      totalTx: 'Total Transactions',
      completed: 'Completed',
      pending: 'Pending',
      failed: 'Failed',
      filterStatus: 'All Statuses',
      filterType: 'All Types',
      noResults: 'No transactions found.',
      failedToLoad: 'Failed to load transactions',
      columns: {
        reference: 'Reference',
        type: 'Type',
        amount: 'Amount',
        fee: 'Fee',
        currency: 'Currency',
        status: 'Status',
        description: 'Description',
        date: 'Date',
      },
    },
    compliance: {
      title: 'Compliance',
      flaggedTx: 'Flagged Transactions',
      callbackLog: 'Callback Log',
      failedRemittances: 'Compliance-Failed Remittances',
      noCallbacks: 'No callbacks received yet.',
      columns: {
        sender: 'Sender',
        amount: 'Amount (XAF)',
        destination: 'Destination',
        channel: 'Channel',
        status: 'Status',
        date: 'Date',
        provider: 'Provider',
        externalRef: 'External Ref',
        remittanceNo: 'Remittance #',
        signature: 'Signature',
        processedAt: 'Processed At',
      },
    },
    kyc: {
      title: 'KYC / KYB Review',
      kycTab: 'KYC (Individuals)',
      kybTab: 'KYB (Businesses)',
      pendingReview: 'Pending Review',
      approved: 'Approved',
      rejected: 'Rejected',
      needsMoreInfo: 'Needs Info',
      searchPlaceholder: 'Search name or ID…',
      noRecords: 'No records found.',
      failedToLoad: 'Failed to load KYC records',
      columns: {
        name: 'Name',
        userId: 'User ID',
        level: 'Level',
        idType: 'ID Type',
        idNumber: 'ID Number',
        submitted: 'Submitted',
        status: 'Status',
        actions: 'Actions',
        company: 'Company',
        rccm: 'RCCM',
        rep: 'Representative',
        sector: 'Sector',
      },
      detail: {
        personalInfo: 'Personal Information',
        documents: 'Documents',
        approve: 'Approve',
        reject: 'Reject',
        needsMoreInfo: 'Needs More Info',
        rejectReason: 'Rejection Reason',
        reviewNotes: 'Review Notes',
        submitting: 'Submitting…',
        fullName: 'Full Name',
        dob: 'Date of Birth',
        address: 'Address',
        city: 'City',
        postalCode: 'Postal Code',
        occupation: 'Occupation',
        monthlyIncome: 'Monthly Income',
        purposeOfUse: 'Purpose of Use',
        sourceOfFunds: 'Source of Funds',
        idDocument: 'ID Document',
        selfie: 'Selfie',
        proofOfAddress: 'Proof of Address',
        proofOfIncome: 'Proof of Income',
        companyName: 'Company Name',
        rccm: 'RCCM Number',
        niu: 'NIU',
        officeAddress: 'Office Address',
        sector: 'Sector',
        repName: 'Representative Name',
        repTitle: 'Representative Title',
        ubo: 'Ultimate Beneficial Owners',
      },
    },
    wallets: {
      title: 'Wallet Management',
      totalWallets: 'Total Wallets',
      fiatWallets: 'Fiat Wallets',
      cryptoWallets: 'Crypto Wallets',
      totalBalance: 'Total Balance',
      searchPlaceholder: 'Search by name or user ID...',
      noWallets: 'No wallets found',
      failedToLoad: 'Failed to load wallets',
      columns: {
        id: 'ID',
        userId: 'User ID',
        name: 'Name',
        type: 'Type',
        currency: 'Currency',
        balance: 'Balance',
        status: 'Status',
      },
    },
    cards: {
      title: 'Card Management',
      totalCards: 'Total Cards',
      virtualCards: 'Virtual Cards',
      physicalCards: 'Physical Cards',
      frozenCards: 'Frozen Cards',
      noCards: 'No cards issued yet',
      failedToLoad: 'Failed to load cards',
      columns: {
        id: 'ID',
        userId: 'User ID',
        cardHolder: 'Card Holder',
        number: 'Number',
        type: 'Type',
        brand: 'Brand',
        balance: 'Balance',
        frozen: 'Frozen',
        online: 'Online',
        contactless: 'Contactless',
      },
    },
    splits: {
      title: 'Split Payments',
      totalSplits: 'Total Splits',
      active: 'Active',
      settled: 'Settled',
      noSplits: 'No split payments',
      failedToLoad: 'Failed to load split payments',
      columns: {
        id: 'ID',
        title: 'Title',
        creator: 'Creator',
        category: 'Category',
        total: 'Total',
        members: 'Members',
        paid: 'Paid',
        status: 'Status',
        created: 'Created',
      },
    },
    chat: {
      title: 'Customer Support Chat',
      openConversations: 'Open Conversations',
      totalMessages: 'Total Messages',
      noConversations: 'No chat conversations yet',
      replyPlaceholder: 'Type a reply...',
    },
    helpArticles: {
      title: 'Help Articles & FAQs',
      totalArticles: 'Total Articles',
      categories: 'Categories',
      mostArticles: 'Most Articles',
      newArticle: 'New Article',
      addArticle: 'Add Article',
      categoryPlaceholder: 'Category',
      orderPlaceholder: 'Order',
      questionPlaceholder: 'Question',
      answerPlaceholder: 'Answer',
      saveEdit: 'Save',
      cancelEdit: 'Cancel',
      deleteConfirm: 'Delete this article?',
      noArticles: 'No help articles yet. Click "Add Article" to create one.',
    },
    corridors: {
      title: 'Corridors',
      newCorridor: 'New Corridor',
      editCorridor: 'Edit Corridor',
      nameRequired: 'Country code, name, and destination currency are required.',
      failedToSave: 'Failed to save',
      columns: {
        country: 'Country',
        currency: 'Currency',
        partner: 'Partner',
        fee: 'Fee',
        region: 'Region',
        status: 'Status',
        actions: 'Actions',
      },
      fields: {
        countryCode: 'Country Code (ISO 2)',
        countryName: 'Country Name',
        flagEmoji: 'Flag Emoji',
        destCurrency: 'Dest Currency',
        partnerName: 'Partner Name',
        outboundRail: 'Outbound Rail',
        feePercent: 'Fee %',
        feeMin: 'Fee Min (XAF)',
        feeMax: 'Fee Max (XAF)',
        settlementTime: 'Settlement Time',
        region: 'Region',
      },
    },
    exchangeRates: {
      title: 'Exchange Rates',
      subtitle: 'Manage currency conversion rates',
      currencyRates: 'Currency Rates',
      columns: {
        from: 'From',
        to: 'To',
        rate: 'Rate',
        spread: 'Spread %',
        lastUpdated: 'Last Updated',
        autoUpdate: 'Auto-Update',
        actions: 'Actions',
      },
    },
    fees: {
      title: 'Fees & Charges',
      subtitle: 'Configure service fees and pricing',
      feeConfigurations: 'Fee Configurations',
      servicePlaceholder: 'Service name',
      addFee: 'Add',
      addNewFee: 'Add New Fee',
      totalActive: 'Total active fee configurations:',
      columns: {
        service: 'Service',
        type: 'Type',
        value: 'Value',
        minFee: 'Min Fee',
        maxFee: 'Max Fee',
        currency: 'Currency',
        enabled: 'Enabled',
        actions: 'Actions',
      },
      types: {
        percentage: 'Percentage',
        flat: 'Flat',
        tiered: 'Tiered',
      },
    },
    countries: {
      title: 'Countries',
      subtitle: 'Manage supported countries and mobile money providers',
      totalCountries: 'Total Countries',
      active: 'Active',
      registrationAllowed: 'Registration Allowed',
      sendAllowed: 'Send Allowed',
      searchPlaceholder: 'Search country...',
      columns: {
        country: 'Country',
        dialCode: 'Dial Code',
        mobileMoneyProviders: 'Mobile Money Providers',
        status: 'Status',
        registration: 'Registration',
        sendMoney: 'Send Money',
      },
      allowed: 'Allowed',
      blocked: 'Blocked',
    },
    settings: {
      title: 'Settings',
      subtitle: 'Configure application-wide settings and preferences',
      general: 'General',
      security: 'Security',
      notifications: 'Notifications',
      limits: 'Transaction Limits',
      payments: 'Payment Methods',
      rates: 'Rate Configuration',
      maintenanceMode: 'Maintenance Mode',
      maintenanceModeDesc: 'Disable app access for all users',
      newRegistrations: 'New Registrations',
      newRegistrationsDesc: 'Allow new users to register',
      emailVerification: 'Email Verification',
      emailVerificationDesc: 'Require email verification on signup',
      smsVerification: 'SMS Verification',
      smsVerificationDesc: 'Require phone verification on signup',
      twoFactorAdmin: 'Admin 2FA',
      twoFactorAdminDesc: 'Require 2FA for all admin accounts',
      autoSuspectFlag: 'Auto-Flag Suspicious',
      autoSuspectFlagDesc: 'Automatically flag suspicious transactions',
      pushNotifications: 'Push Notifications',
      pushNotificationsDesc: 'Enable push notifications for users',
      emailNotifications: 'Email Notifications',
      emailNotificationsDesc: 'Send email notifications for transactions',
      maxDailyTransfer: 'Max Daily Transfer',
      maxSingleTransfer: 'Max Single Transfer',
      minTransfer: 'Min Transfer',
      sessionTimeout: 'Session Timeout (min)',
      defaultCurrency: 'Default Currency',
      kycRequired: 'KYC Required',
      kycRequiredDesc: 'Require KYC before transactions',
      cryptoEnabled: 'Crypto Enabled',
      cryptoEnabledDesc: 'Enable cryptocurrency features',
      cardPayments: 'Card Payments',
      cardPaymentsDesc: 'Enable card payment processing',
      bankTransfers: 'Bank Transfers',
      bankTransfersDesc: 'Enable bank transfer payments',
      mobileMoney: 'Mobile Money',
      mobileMoneyDesc: 'Enable mobile money payments',
      autoRateUpdate: 'Auto Rate Update',
      autoRateUpdateDesc: 'Automatically update exchange rates',
      rateUpdateInterval: 'Rate Update Interval (min)',
      highValueThreshold: 'High-Value Threshold',
      highValueThresholdDesc: 'Flag transactions above this amount',
      saveSettings: 'Save Settings',
      settingsSaved: 'Settings saved successfully!',
    },
    crypto: {
      title: 'Crypto Settings',
      subtitle: 'Manage Solana blockchain and crypto wallet configuration',
      totalWallets: 'Total Wallets',
      activeWallets: 'Active Wallets',
      flagged: 'Flagged',
      frozen: 'Frozen',
      cryptoModuleEnabled: 'Crypto Module Enabled',
      cryptoModuleEnabledDesc: 'Master toggle for all crypto features',
      autoApproveWithdrawals: 'Auto-Approve Withdrawals',
      autoApproveWithdrawalsDesc: 'Skip manual review for small amounts',
      kycRequired: 'KYC Required',
      kycRequiredDesc: 'Require verified KYC before crypto use',
      maxDailyWithdrawal: 'Max Daily Withdrawal (USD)',
      maxDailyWithdrawalDesc: 'Per-user daily withdrawal limit',
      confirmationsRequired: 'Confirmations Required',
      confirmationsRequiredDesc: 'Solana block confirmations before crediting',
      antiFraud: 'Anti-Fraud Detection',
      antiFraudDesc: 'Enable ML-based fraud detection',
      columns: {
        symbol: 'Symbol',
        name: 'Name',
        enabled: 'Enabled',
        minWithdraw: 'Min Withdraw',
        maxWithdraw: 'Max Withdraw',
        fee: 'Fee',
      },
      saveSuccess: 'Crypto settings saved!',
    },
    adminAccounts: {
      title: 'Admin Accounts',
      subtitle: 'Manage administrator access and permissions',
      addAdmin: 'Add Admin',
      addNewAdmin: 'Add New Admin',
      totalAdmins: 'Total Admins',
      with2FA: 'With 2FA',
      backToAccounts: 'Back to Admin Accounts',
      noAdmins: 'No admins found matching your filters.',
      searchPlaceholder: 'Search by name or email...',
      allRoles: 'All Roles',
      columns: {
        admin: 'Admin',
        role: 'Role',
        twoFactor: '2FA',
        lastLogin: 'Last Login',
        status: 'Status',
        actions: 'Actions',
      },
      roles: {
        super_admin: 'Super Admin',
        finance: 'Finance Team',
        customer_service: 'Customer Service',
        compliance: 'Compliance Team',
      },
      detail: {
        email: 'Email',
        password: 'Password',
        role: 'Role',
        twoFactor: '2FA Status',
        created: 'Created',
        lastLogin: 'Last Login',
        accountStatus: 'Account Status',
        permissions: 'Permissions',
        disableAccount: 'Disable Account',
        enableAccount: 'Enable Account',
        resetPassword: 'Reset Password',
        twoFactorEnabled: 'Enabled',
        twoFactorDisabled: 'Disabled',
      },
      errors: {
        fillAllFields: 'Please fill in name, email, and password.',
        failedToCreate: 'Failed to create admin.',
      },
    },
    auditLog: {
      title: 'Audit Log',
      subtitle: 'Track all administrative actions and system events',
      totalEntries: 'Total Entries',
      today: 'Today',
      warnings: 'Warnings',
      critical: 'Critical',
      searchPlaceholder: 'Search actions, admins, details...',
      filterCategory: 'All Categories',
      filterSeverity: 'All Severities',
      columns: {
        id: 'ID',
        timestamp: 'Timestamp',
        admin: 'Admin',
        action: 'Action',
        category: 'Category',
        severity: 'Severity',
        ip: 'IP Address',
        details: 'Details',
      },
    },
    profile: {
      title: 'My Profile',
      editProfile: 'Edit Profile',
      saveProfile: 'Save Profile',
      cancel: 'Cancel',
      changePassword: 'Change Password',
      currentPassword: 'Current Password',
      newPassword: 'New Password',
      confirmPassword: 'Confirm Password',
      updatePassword: 'Update Password',
      securitySettings: 'Security Settings',
      twoFactor: 'Two-Factor Authentication',
      twoFactorDesc: 'Add an extra layer of security',
      emailAlerts: 'Email Alerts',
      emailAlertsDesc: 'Receive security alerts via email',
      loginAlerts: 'Login Alerts',
      loginAlertsDesc: 'Get notified of new login activity',
      fields: {
        fullName: 'Full Name',
        email: 'Email',
        phone: 'Phone',
        role: 'Role',
        memberSince: 'Member Since',
        lastLogin: 'Last Login',
      },
      success: {
        profileUpdated: 'Profile updated successfully!',
        passwordChanged: 'Password changed successfully!',
        settingsSaved: 'Security settings saved!',
      },
      errors: {
        fillAllFields: 'Please fill in all password fields.',
        passwordMismatch: 'New passwords do not match.',
        passwordTooShort: 'Password must be at least 8 characters.',
      },
    },
  },
  fr: {
    common: {
      search: 'Rechercher...',
      refresh: 'Actualiser',
      loading: 'Chargement…',
      save: 'Enregistrer',
      cancel: 'Annuler',
      delete: 'Supprimer',
      edit: 'Modifier',
      close: 'Fermer',
      actions: 'Actions',
      status: 'Statut',
      active: 'Actif',
      inactive: 'Inactif',
      enabled: 'Activé',
      disabled: 'Désactivé',
      yes: 'Oui',
      no: 'Non',
      date: 'Date',
      type: 'Type',
      amount: 'Montant',
      currency: 'Devise',
      id: 'ID',
      name: 'Nom',
      email: 'Email',
      phone: 'Téléphone',
      country: 'Pays',
      noData: 'Aucune donnée disponible',
      allowed: 'Autorisé',
      blocked: 'Bloqué',
      all: 'Tous',
      pending: 'En attente',
      approved: 'Approuvé',
      rejected: 'Rejeté',
      completed: 'Complété',
      failed: 'Échoué',
      cancelled: 'Annulé',
      verified: 'Vérifié',
      suspended: 'Suspendu',
      notSubmitted: 'Non soumis',
      markAllRead: 'Tout marquer comme lu',
      notifications: 'Notifications',
      myProfile: 'Mon Profil',
      settings: 'Paramètres',
      logOut: 'Se déconnecter',
      superAdmin: 'Super Administrateur',
      saving: 'Enregistrement…',
      on: 'On',
      off: 'Off',
      view: 'Voir',
      details: 'Détails',
      address: 'Adresse',
      city: 'Ville',
      back: 'Retour',
      add: 'Ajouter',
      update: 'Mettre à jour',
      agent: 'Agent',
    },
    sidebar: {
      main: 'PRINCIPAL',
      management: 'GESTION',
      configuration: 'CONFIGURATION',
      support: 'SUPPORT',
      system: 'SYSTÈME',
      dashboard: 'Tableau de bord',
      analytics: 'Analytiques',
      users: 'Utilisateurs',
      agents: 'Agents',
      agencies: 'Agences',
      transactions: 'Transactions',
      compliance: 'Conformité',
      kycReview: 'Révision KYC',
      wallets: 'Portefeuilles',
      cards: 'Cartes',
      splitPayments: 'Paiements partagés',
      chatSupport: 'Support Chat',
      helpArticles: "Articles d'aide",
      corridors: 'Corridors',
      exchangeRates: 'Taux de change',
      feesCharges: 'Frais et charges',
      countries: 'Pays',
      cryptoSettings: 'Paramètres crypto',
      adminAccounts: 'Comptes admin',
      auditLog: "Journal d'audit",
    },
    login: {
      title: 'Tableau de bord Admin',
      subtitle: 'Connectez-vous pour gérer votre plateforme',
      emailLabel: 'Adresse e-mail',
      emailPlaceholder: 'admin@moneyplus.cd',
      passwordLabel: 'Mot de passe',
      passwordPlaceholder: 'Entrez votre mot de passe',
      forgotPassword: 'Mot de passe oublié ?',
      signIn: 'Se connecter',
      signingIn: 'Connexion en cours...',
      emailRequired: 'Veuillez saisir votre e-mail et mot de passe.',
      invalidCredentials: 'E-mail ou mot de passe invalide.',
      noToken: 'Connexion réussie mais aucun jeton retourné par le serveur.',
      loginFailed: 'Connexion échouée. Veuillez réessayer.',
      forgotMessage: 'Lien de réinitialisation envoyé à votre e-mail.',
      version: 'Money+ Congo Panel Admin v1.0',
    },
    dashboard: {
      title: 'Tableau de bord',
      subtitle: 'Aperçu de Money+ Congo',
      totalUsers: 'Utilisateurs totaux',
      totalTransactions: 'Transactions totales',
      transactionVolume: 'Volume des transactions',
      revenue: 'Revenus (Frais)',
      dailyVolume: 'Volume journalier (7 derniers jours)',
      byType: 'Transactions par type',
      topCountries: 'Principaux pays (Utilisateurs)',
      recentTransactions: 'Transactions récentes',
      noRecentTx: 'Aucune transaction récente.',
      failedToLoad: 'Échec du chargement du tableau de bord',
    },
    analytics: {
      title: 'Analytiques',
      subtitle: 'Métriques en direct depuis le backend',
      totalVolume: 'Volume total (CDF)',
      activeUsers: 'Utilisateurs actifs',
      totalTransactions: 'Transactions totales',
      totalFees: 'Frais totaux (CDF)',
      byType: 'Transactions par type',
      topCountries: 'Principaux pays',
      dailyCount: 'Transactions journalières',
      failedToLoad: 'Échec du chargement des analytiques',
    },
    users: {
      title: 'Gestion des utilisateurs',
      searchPlaceholder: 'Rechercher des utilisateurs...',
      addUser: 'Ajouter un utilisateur',
      addUserDesc: 'Créer un nouveau compte utilisateur',
      countryCode: 'Indicatif du pays',
      password: 'Mot de passe',
      avatarUrl: "URL de l'avatar",
      kycPhotos: 'Photos KYC',
      kycBack: 'Dos de la pièce',
      totalUsers: 'Utilisateurs totaux',
      activeUsers: 'Utilisateurs actifs',
      suspended: 'Suspendus',
      pendingKyc: 'KYC en attente',
      noUsers: 'Aucun utilisateur trouvé.',
      failedToLoad: 'Échec du chargement des utilisateurs',
      columns: {
        user: 'Utilisateur',
        phone: 'Téléphone',
        country: 'Pays',
        kyc: 'KYC',
        walletBalance: 'Solde portefeuille',
        status: 'Statut',
        joined: 'Inscrit le',
        actions: 'Actions',
      },
      kycStatus: {
        verified: 'Vérifié',
        pending: 'En attente',
        rejected: 'Rejeté',
        not_submitted: 'Non soumis',
      },
      userStatus: {
        active: 'Actif',
        suspended: 'Suspendu',
        pending: 'En attente',
      },
      detail: {
        personalInfo: 'Informations personnelles',
        wallets: 'Portefeuilles',
        transactions: 'Transactions récentes',
        kycDetails: 'Détails KYC',
        fullName: 'Nom complet',
        email: 'Email',
        phone: 'Téléphone',
        country: 'Pays',
        joined: 'Inscrit le',
        walletBalance: 'Solde portefeuille',
        kycStatus: 'Statut KYC',
        userStatus: 'Statut du compte',
        suspend: "Suspendre l'utilisateur",
        activate: "Activer l'utilisateur",
        noTransactions: 'Aucune transaction.',
        noWallets: 'Aucun portefeuille.',
      },
    },
    agents: {
      title: 'Gestion des agents',
      searchPlaceholder: 'Rechercher des agents...',
      totalAgents: 'Agents totaux',
      totalCommissions: 'Commissions totales',
      todayTx: "Transactions d'aujourd'hui",
      activeFloat: 'Float actif',
      noAgents: 'Aucun agent trouvé',
      cashFloat: 'Float en espèces',
      totalCommission: 'Commission totale',
      recentTx: "Transactions récentes de l'agent",
      commissionHistory: 'Historique des commissions',
      columns: {
        agent: 'Agent',
        phone: 'Téléphone',
        country: 'Pays',
        cashFloat: 'Float en espèces',
        eMoneyFloat: 'Float e-Money',
        commission: 'Commission',
        todayTxns: "Txns aujourd'hui",
        actions: 'Actions',
      },
      failedToLoad: 'Échec du chargement des agents',
    },
    agencies: {
      title: 'Gestion des agences',
      newAgency: 'Nouvelle agence',
      editAgency: "Modifier l'agence",
      searchPlaceholder: 'Rechercher par nom, ville ou code…',
      nameRequired: 'Le nom et la ville sont requis.',
      failedToSave: "Échec de l'enregistrement",
      columns: {
        code: 'Code',
        name: 'Nom',
        type: 'Type',
        city: 'Ville',
        manager: 'Gestionnaire',
        status: 'Statut',
        actions: 'Actions',
      },
      types: {
        agency: 'Agence',
        partner_branch: 'Succursale partenaire',
        mobile_agent: 'Agent mobile',
      },
      fields: {
        type: 'Type',
        name: 'Nom',
        city: 'Ville',
        address: 'Adresse',
        managerName: 'Nom du gestionnaire',
        managerPhone: 'Téléphone du gestionnaire',
        cashLimit: 'Limite de caisse (XAF)',
        openingHours: "Heures d'ouverture",
        agentUserId: 'ID utilisateur agent (optionnel)',
      },
    },
    transactions: {
      title: 'Transactions',
      searchPlaceholder: 'Rechercher par référence ou description...',
      totalTx: 'Transactions totales',
      completed: 'Complétées',
      pending: 'En attente',
      failed: 'Échouées',
      filterStatus: 'Tous les statuts',
      filterType: 'Tous les types',
      noResults: 'Aucune transaction trouvée.',
      failedToLoad: 'Échec du chargement des transactions',
      columns: {
        reference: 'Référence',
        type: 'Type',
        amount: 'Montant',
        fee: 'Frais',
        currency: 'Devise',
        status: 'Statut',
        description: 'Description',
        date: 'Date',
      },
    },
    compliance: {
      title: 'Conformité',
      flaggedTx: 'Transactions signalées',
      callbackLog: 'Journal des callbacks',
      failedRemittances: 'Remises en échec de conformité',
      noCallbacks: 'Aucun callback reçu.',
      columns: {
        sender: 'Expéditeur',
        amount: 'Montant (XAF)',
        destination: 'Destination',
        channel: 'Canal',
        status: 'Statut',
        date: 'Date',
        provider: 'Fournisseur',
        externalRef: 'Réf. externe',
        remittanceNo: 'Remise n°',
        signature: 'Signature',
        processedAt: 'Traité le',
      },
    },
    kyc: {
      title: 'Révision KYC / KYB',
      kycTab: 'KYC (Personnes physiques)',
      kybTab: 'KYB (Entreprises)',
      pendingReview: 'En attente de révision',
      approved: 'Approuvé',
      rejected: 'Rejeté',
      needsMoreInfo: 'Infos manquantes',
      searchPlaceholder: 'Rechercher par nom ou ID…',
      noRecords: 'Aucun enregistrement trouvé.',
      failedToLoad: 'Échec du chargement des enregistrements KYC',
      columns: {
        name: 'Nom',
        userId: 'ID utilisateur',
        level: 'Niveau',
        idType: "Type d'identité",
        idNumber: "Numéro d'identité",
        submitted: 'Soumis le',
        status: 'Statut',
        actions: 'Actions',
        company: 'Entreprise',
        rccm: 'RCCM',
        rep: 'Représentant',
        sector: 'Secteur',
      },
      detail: {
        personalInfo: 'Informations personnelles',
        documents: 'Documents',
        approve: 'Approuver',
        reject: 'Rejeter',
        needsMoreInfo: 'Informations manquantes',
        rejectReason: 'Raison du rejet',
        reviewNotes: 'Notes de révision',
        submitting: 'Soumission en cours…',
        fullName: 'Nom complet',
        dob: 'Date de naissance',
        address: 'Adresse',
        city: 'Ville',
        postalCode: 'Code postal',
        occupation: 'Profession',
        monthlyIncome: 'Revenu mensuel',
        purposeOfUse: "Objet d'utilisation",
        sourceOfFunds: 'Source des fonds',
        idDocument: "Document d'identité",
        selfie: 'Selfie',
        proofOfAddress: 'Justificatif de domicile',
        proofOfIncome: 'Justificatif de revenus',
        companyName: 'Nom de la société',
        rccm: 'Numéro RCCM',
        niu: 'NIU',
        officeAddress: 'Adresse du bureau',
        sector: 'Secteur',
        repName: 'Nom du représentant',
        repTitle: 'Titre du représentant',
        ubo: 'Bénéficiaires effectifs',
      },
    },
    wallets: {
      title: 'Gestion des portefeuilles',
      totalWallets: 'Portefeuilles totaux',
      fiatWallets: 'Portefeuilles fiat',
      cryptoWallets: 'Portefeuilles crypto',
      totalBalance: 'Solde total',
      searchPlaceholder: 'Rechercher par nom ou ID utilisateur...',
      noWallets: 'Aucun portefeuille trouvé',
      failedToLoad: 'Échec du chargement des portefeuilles',
      columns: {
        id: 'ID',
        userId: 'ID utilisateur',
        name: 'Nom',
        type: 'Type',
        currency: 'Devise',
        balance: 'Solde',
        status: 'Statut',
      },
    },
    cards: {
      title: 'Gestion des cartes',
      totalCards: 'Cartes totales',
      virtualCards: 'Cartes virtuelles',
      physicalCards: 'Cartes physiques',
      frozenCards: 'Cartes gelées',
      noCards: 'Aucune carte émise',
      failedToLoad: 'Échec du chargement des cartes',
      columns: {
        id: 'ID',
        userId: 'ID utilisateur',
        cardHolder: 'Titulaire',
        number: 'Numéro',
        type: 'Type',
        brand: 'Marque',
        balance: 'Solde',
        frozen: 'Gelée',
        online: 'En ligne',
        contactless: 'Sans contact',
      },
    },
    splits: {
      title: 'Paiements partagés',
      totalSplits: 'Partages totaux',
      active: 'Actifs',
      settled: 'Réglés',
      noSplits: 'Aucun paiement partagé',
      failedToLoad: 'Échec du chargement des paiements partagés',
      columns: {
        id: 'ID',
        title: 'Titre',
        creator: 'Créateur',
        category: 'Catégorie',
        total: 'Total',
        members: 'Membres',
        paid: 'Payé',
        status: 'Statut',
        created: 'Créé le',
      },
    },
    chat: {
      title: 'Chat de support client',
      openConversations: 'Conversations ouvertes',
      totalMessages: 'Messages totaux',
      noConversations: 'Aucune conversation de chat',
      replyPlaceholder: 'Tapez une réponse...',
    },
    helpArticles: {
      title: "Articles d'aide et FAQ",
      totalArticles: 'Articles totaux',
      categories: 'Catégories',
      mostArticles: "Plus d'articles",
      newArticle: 'Nouvel article',
      addArticle: 'Ajouter un article',
      categoryPlaceholder: 'Catégorie',
      orderPlaceholder: 'Ordre',
      questionPlaceholder: 'Question',
      answerPlaceholder: 'Réponse',
      saveEdit: 'Enregistrer',
      cancelEdit: 'Annuler',
      deleteConfirm: 'Supprimer cet article ?',
      noArticles: 'Aucun article d\'aide. Cliquez sur "Ajouter un article" pour en créer un.',
    },
    corridors: {
      title: 'Corridors',
      newCorridor: 'Nouveau corridor',
      editCorridor: 'Modifier le corridor',
      nameRequired: 'Le code pays, le nom et la devise de destination sont requis.',
      failedToSave: "Échec de l'enregistrement",
      columns: {
        country: 'Pays',
        currency: 'Devise',
        partner: 'Partenaire',
        fee: 'Frais',
        region: 'Région',
        status: 'Statut',
        actions: 'Actions',
      },
      fields: {
        countryCode: 'Code pays (ISO 2)',
        countryName: 'Nom du pays',
        flagEmoji: 'Emoji drapeau',
        destCurrency: 'Devise de destination',
        partnerName: 'Nom du partenaire',
        outboundRail: 'Rail sortant',
        feePercent: 'Frais %',
        feeMin: 'Frais min (XAF)',
        feeMax: 'Frais max (XAF)',
        settlementTime: 'Délai de règlement',
        region: 'Région',
      },
    },
    exchangeRates: {
      title: 'Taux de change',
      subtitle: 'Gérer les taux de conversion des devises',
      currencyRates: 'Taux de change',
      columns: {
        from: 'De',
        to: 'Vers',
        rate: 'Taux',
        spread: 'Spread %',
        lastUpdated: 'Dernière mise à jour',
        autoUpdate: 'Mise à jour auto',
        actions: 'Actions',
      },
    },
    fees: {
      title: 'Frais et charges',
      subtitle: 'Configurer les frais de service et la tarification',
      feeConfigurations: 'Configurations des frais',
      servicePlaceholder: 'Nom du service',
      addFee: 'Ajouter',
      addNewFee: 'Ajouter des frais',
      totalActive: 'Total des configurations de frais actives :',
      columns: {
        service: 'Service',
        type: 'Type',
        value: 'Valeur',
        minFee: 'Frais min',
        maxFee: 'Frais max',
        currency: 'Devise',
        enabled: 'Activé',
        actions: 'Actions',
      },
      types: {
        percentage: 'Pourcentage',
        flat: 'Fixe',
        tiered: 'Par palier',
      },
    },
    countries: {
      title: 'Pays',
      subtitle: 'Gérer les pays et fournisseurs de mobile money',
      totalCountries: 'Pays totaux',
      active: 'Actifs',
      registrationAllowed: 'Inscription autorisée',
      sendAllowed: 'Envoi autorisé',
      searchPlaceholder: 'Rechercher un pays...',
      columns: {
        country: 'Pays',
        dialCode: 'Indicatif',
        mobileMoneyProviders: 'Fournisseurs mobile money',
        status: 'Statut',
        registration: 'Inscription',
        sendMoney: "Envoi d'argent",
      },
      allowed: 'Autorisé',
      blocked: 'Bloqué',
    },
    settings: {
      title: 'Paramètres',
      subtitle: "Configurer les paramètres et préférences de l'application",
      general: 'Général',
      security: 'Sécurité',
      notifications: 'Notifications',
      limits: 'Limites de transactions',
      payments: 'Méthodes de paiement',
      rates: 'Configuration des taux',
      maintenanceMode: 'Mode maintenance',
      maintenanceModeDesc: "Désactiver l'accès à l'app pour tous les utilisateurs",
      newRegistrations: 'Nouvelles inscriptions',
      newRegistrationsDesc: "Permettre aux nouveaux utilisateurs de s'inscrire",
      emailVerification: 'Vérification e-mail',
      emailVerificationDesc: "Exiger la vérification e-mail à l'inscription",
      smsVerification: 'Vérification SMS',
      smsVerificationDesc: "Exiger la vérification téléphonique à l'inscription",
      twoFactorAdmin: '2FA Admin',
      twoFactorAdminDesc: 'Exiger la 2FA pour tous les comptes admin',
      autoSuspectFlag: 'Signalement automatique',
      autoSuspectFlagDesc: 'Signaler automatiquement les transactions suspectes',
      pushNotifications: 'Notifications push',
      pushNotificationsDesc: 'Activer les notifications push pour les utilisateurs',
      emailNotifications: 'Notifications e-mail',
      emailNotificationsDesc: 'Envoyer des notifications e-mail pour les transactions',
      maxDailyTransfer: 'Transfert journalier max',
      maxSingleTransfer: 'Transfert unique max',
      minTransfer: 'Transfert minimum',
      sessionTimeout: 'Expiration de session (min)',
      defaultCurrency: 'Devise par défaut',
      kycRequired: 'KYC obligatoire',
      kycRequiredDesc: 'Exiger le KYC avant les transactions',
      cryptoEnabled: 'Crypto activé',
      cryptoEnabledDesc: 'Activer les fonctionnalités crypto',
      cardPayments: 'Paiements par carte',
      cardPaymentsDesc: 'Activer le traitement des paiements par carte',
      bankTransfers: 'Virements bancaires',
      bankTransfersDesc: 'Activer les virements bancaires',
      mobileMoney: 'Mobile Money',
      mobileMoneyDesc: 'Activer les paiements mobile money',
      autoRateUpdate: 'Mise à jour auto des taux',
      autoRateUpdateDesc: 'Mettre à jour automatiquement les taux de change',
      rateUpdateInterval: 'Intervalle de mise à jour (min)',
      highValueThreshold: 'Seuil de haute valeur',
      highValueThresholdDesc: 'Signaler les transactions au-dessus de ce montant',
      saveSettings: 'Enregistrer les paramètres',
      settingsSaved: 'Paramètres enregistrés avec succès !',
    },
    crypto: {
      title: 'Paramètres crypto',
      subtitle: 'Gérer la blockchain Solana et la configuration des portefeuilles crypto',
      totalWallets: 'Portefeuilles totaux',
      activeWallets: 'Portefeuilles actifs',
      flagged: 'Signalés',
      frozen: 'Gelés',
      cryptoModuleEnabled: 'Module crypto activé',
      cryptoModuleEnabledDesc: 'Interrupteur principal pour toutes les fonctionnalités crypto',
      autoApproveWithdrawals: 'Auto-approbation des retraits',
      autoApproveWithdrawalsDesc: 'Ignorer la révision manuelle pour les petits montants',
      kycRequired: 'KYC obligatoire',
      kycRequiredDesc: "Exiger un KYC vérifié avant l'utilisation des crypto",
      maxDailyWithdrawal: 'Retrait journalier max (USD)',
      maxDailyWithdrawalDesc: 'Limite de retrait journalier par utilisateur',
      confirmationsRequired: 'Confirmations requises',
      confirmationsRequiredDesc: 'Confirmations de blocs Solana avant crédit',
      antiFraud: 'Détection anti-fraude',
      antiFraudDesc: 'Activer la détection de fraude par ML',
      columns: {
        symbol: 'Symbole',
        name: 'Nom',
        enabled: 'Activé',
        minWithdraw: 'Retrait min',
        maxWithdraw: 'Retrait max',
        fee: 'Frais',
      },
      saveSuccess: 'Paramètres crypto enregistrés !',
    },
    adminAccounts: {
      title: 'Comptes administrateurs',
      subtitle: 'Gérer les accès administrateur et les permissions',
      addAdmin: 'Ajouter un admin',
      addNewAdmin: 'Ajouter un nouvel admin',
      totalAdmins: 'Total des admins',
      with2FA: 'Avec 2FA',
      backToAccounts: 'Retour aux comptes admin',
      noAdmins: 'Aucun admin trouvé correspondant à vos filtres.',
      searchPlaceholder: 'Rechercher par nom ou email...',
      allRoles: 'Tous les rôles',
      columns: {
        admin: 'Administrateur',
        role: 'Rôle',
        twoFactor: '2FA',
        lastLogin: 'Dernière connexion',
        status: 'Statut',
        actions: 'Actions',
      },
      roles: {
        super_admin: 'Super Admin',
        finance: 'Équipe finance',
        customer_service: 'Service client',
        compliance: 'Équipe conformité',
      },
      detail: {
        email: 'Email',
        password: 'Mot de passe',
        role: 'Rôle',
        twoFactor: 'Statut 2FA',
        created: 'Créé le',
        lastLogin: 'Dernière connexion',
        accountStatus: 'Statut du compte',
        permissions: 'Permissions',
        disableAccount: 'Désactiver le compte',
        enableAccount: 'Activer le compte',
        resetPassword: 'Réinitialiser le mot de passe',
        twoFactorEnabled: 'Activé',
        twoFactorDisabled: 'Désactivé',
      },
      errors: {
        fillAllFields: "Veuillez renseigner le nom, l'email et le mot de passe.",
        failedToCreate: "Échec de la création de l'admin.",
      },
    },
    auditLog: {
      title: "Journal d'audit",
      subtitle: 'Suivre toutes les actions administratives et événements système',
      totalEntries: 'Entrées totales',
      today: "Aujourd'hui",
      warnings: 'Avertissements',
      critical: 'Critique',
      searchPlaceholder: 'Rechercher actions, admins, détails...',
      filterCategory: 'Toutes les catégories',
      filterSeverity: 'Toutes les sévérités',
      columns: {
        id: 'ID',
        timestamp: 'Horodatage',
        admin: 'Admin',
        action: 'Action',
        category: 'Catégorie',
        severity: 'Sévérité',
        ip: 'Adresse IP',
        details: 'Détails',
      },
    },
    profile: {
      title: 'Mon Profil',
      editProfile: 'Modifier le profil',
      saveProfile: 'Enregistrer le profil',
      cancel: 'Annuler',
      changePassword: 'Changer le mot de passe',
      currentPassword: 'Mot de passe actuel',
      newPassword: 'Nouveau mot de passe',
      confirmPassword: 'Confirmer le mot de passe',
      updatePassword: 'Mettre à jour le mot de passe',
      securitySettings: 'Paramètres de sécurité',
      twoFactor: 'Authentification à deux facteurs',
      twoFactorDesc: 'Ajouter une couche de sécurité supplémentaire',
      emailAlerts: 'Alertes e-mail',
      emailAlertsDesc: 'Recevoir les alertes de sécurité par e-mail',
      loginAlerts: 'Alertes de connexion',
      loginAlertsDesc: 'Être notifié des nouvelles connexions',
      fields: {
        fullName: 'Nom complet',
        email: 'Email',
        phone: 'Téléphone',
        role: 'Rôle',
        memberSince: 'Membre depuis',
        lastLogin: 'Dernière connexion',
      },
      success: {
        profileUpdated: 'Profil mis à jour avec succès !',
        passwordChanged: 'Mot de passe changé avec succès !',
        settingsSaved: 'Paramètres de sécurité enregistrés !',
      },
      errors: {
        fillAllFields: 'Veuillez remplir tous les champs de mot de passe.',
        passwordMismatch: 'Les nouveaux mots de passe ne correspondent pas.',
        passwordTooShort: 'Le mot de passe doit comporter au moins 8 caractères.',
      },
    },
  },
} as const;

export type TranslationKeys = typeof translations.en;
